import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Home, Sun, Settings, Droplets, Check, ClipboardCheck, FileText, Hammer, BadgeCheck } from 'lucide-react';
import { services } from '../mock';
import { PROCESS_ICON_SIZE } from '../constants/icon-sizes';

const iconMap = {
  wrench: Wrench,
  home: Home,
  sun: Sun,
  settings: Settings,
  droplets: Droplets,
};

const processSteps = [
  { number: '01', icon: ClipboardCheck, title: 'Consult & Inspect',
    description: "We start with a thorough on-site inspection to assess your needs, identify the issue, and understand exactly what you're after." },
  { number: '02', icon: FileText, title: 'Proposal & Estimate',
    description: "You'll receive a clear, upfront written quote with no hidden fees — outlining the scope, materials, timeline, and total cost." },
  { number: '03', icon: Hammer, title: 'Installation or Repair',
    description: 'Our licensed team carries out the work to the highest standard, using quality materials and respecting your home or business.' },
  { number: '04', icon: BadgeCheck, title: 'Final Check & Follow-Up',
    description: "We test everything, walk you through the work, clean up the site, and follow up afterwards to make sure you're 100% satisfied." },
];

const ServiceCard = ({ service }) => {
  const IconComponent = iconMap[service.icon];
  return (
    <div
      data-testid={`service-card-${service.id}`}
      className="bg-white rounded-2xl p-8 md:p-10 border border-slate-200 hover:border-blue-200 transition-all duration-300 hover:shadow-xl group"
    >
      <div className="flex items-start gap-5 mb-6">
        <div className="bg-blue-50 w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-300">
          <IconComponent className="text-blue-600 group-hover:text-white transition-colors duration-300" size={32} />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{service.title}</h3>
          <p className="text-slate-600 leading-relaxed">{service.description}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-6 pt-6 border-t border-slate-100">
        {service.features.map((feature) => (
          <div key={feature} className="flex items-start gap-2">
            <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-slate-700 text-sm">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProcessStepCard = ({ step, index }) => {
  const Icon = step.icon;
  return (
    <div
      data-testid={`process-step-${index + 1}`}
      className="relative bg-white border border-slate-200 rounded-2xl p-7 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
    >
      <div className="absolute top-4 right-5 text-5xl font-bold text-slate-100 select-none">{step.number}</div>
      <div className="relative">
        <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mb-5 shadow-md">
          <Icon className="text-white" size={PROCESS_ICON_SIZE} />
        </div>
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5">Step {step.number}</div>
        <h4 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h4>
        <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
      </div>
    </div>
  );
};

const ProcessGrid = () => (
  <div className="mt-24">
    <div className="text-center mb-14">
      <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Our Process</span>
      <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How We Work</h3>
      <p className="text-lg text-slate-600 max-w-2xl mx-auto">
        A simple, transparent 4-step process — so you always know what to expect
      </p>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
      {processSteps.map((step, index) => (
        <ProcessStepCard key={step.number} step={step} index={index} />
      ))}
    </div>
  </div>
);

const ServicesSection = () => {
  const navigate = useNavigate();
  return (
    <section id="services" data-testid="services-section" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            What We Offer
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Our Services</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Comprehensive plumbing solutions for residential and commercial properties across Melbourne
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        <div className="text-center mt-14">
          <p className="text-slate-600 mb-5 text-lg">
            Need a service not listed? We do it all — get in touch.
          </p>
          <button
            onClick={() => navigate('/contact')}
            data-testid="services-contact-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Contact Us
          </button>
        </div>

        <ProcessGrid />
      </div>
    </section>
  );
};

export default ServicesSection;
